/**
 * The slice of the Graphics drawing API the terrain painters use. `Phaser.GameObjects.Graphics`
 * satisfies it structurally, so the same painter can draw into a Graphics command buffer
 * (replayed every frame) or into `MeshPaintSink` (tessellated once for a retained
 * `StaticMesh`). See docs/PERFORMANCE.md.
 */
import type { Vec2 } from '../types.ts';
import { TriMeshBuilder, packTint, type MeshChunk, type Triangulate } from './meshBuilder.ts';

export interface PaintSink {
  fillStyle(color: number, alpha?: number): unknown;
  lineStyle(width: number, color: number, alpha?: number): unknown;
  fillPoints(points: readonly Vec2[], closeShape?: boolean, closePath?: boolean): unknown;
  strokePoints(points: readonly Vec2[], closeShape?: boolean, closePath?: boolean): unknown;
  fillTriangle(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): unknown;
  fillEllipse(x: number, y: number, width: number, height: number, smoothness?: number): unknown;
  fillCircle(x: number, y: number, radius: number): unknown;
  strokeCircle(x: number, y: number, radius: number): unknown;
  lineBetween(x0: number, y0: number, x1: number, y1: number): unknown;
}

/** Rim samples for a baked circle. Graphics walks ~100 arc steps; 48 is indistinguishable at pole size. */
export const CIRCLE_SMOOTHNESS = 48;

function circlePoints(x: number, y: number, radius: number): Vec2[] {
  const points: Vec2[] = [];

  for (let i = 0; i < CIRCLE_SMOOTHNESS; i += 1) {
    const angle = (i / CIRCLE_SMOOTHNESS) * Math.PI * 2;
    points.push({ x: x + Math.cos(angle) * radius, y: y + Math.sin(angle) * radius });
  }

  return points;
}

/** Records the current fill / line style and forwards each primitive to a `TriMeshBuilder`. */
export class MeshPaintSink implements PaintSink {
  private readonly mesh: TriMeshBuilder;
  private fillTint = packTint(0xffffff, 1);
  private lineTint = packTint(0xffffff, 1);
  private lineWidth = 1;

  constructor(triangulate: Triangulate) {
    this.mesh = new TriMeshBuilder(triangulate);
  }

  get isEmpty(): boolean {
    return this.mesh.isEmpty;
  }

  fillStyle(color: number, alpha = 1): this {
    this.fillTint = packTint(color, alpha);
    return this;
  }

  lineStyle(width: number, color: number, alpha = 1): this {
    this.lineWidth = width;
    this.lineTint = packTint(color, alpha);
    return this;
  }

  fillPoints(points: readonly Vec2[]): this {
    this.mesh.fillPolygon(points, this.fillTint);
    return this;
  }

  strokePoints(points: readonly Vec2[], closeShape = false, closePath = false): this {
    this.mesh.strokePolyline(points, this.lineWidth, closeShape || closePath, this.lineTint);
    return this;
  }

  fillTriangle(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): this {
    this.mesh.fillTriangle(x0, y0, x1, y1, x2, y2, this.fillTint);
    return this;
  }

  fillEllipse(x: number, y: number, width: number, height: number, smoothness?: number): this {
    this.mesh.fillEllipse(x, y, width, height, this.fillTint, smoothness);
    return this;
  }

  fillCircle(x: number, y: number, radius: number): this {
    this.mesh.fillEllipse(x, y, radius * 2, radius * 2, this.fillTint, CIRCLE_SMOOTHNESS);
    return this;
  }

  strokeCircle(x: number, y: number, radius: number): this {
    this.mesh.strokePolyline(circlePoints(x, y, radius), this.lineWidth, true, this.lineTint);
    return this;
  }

  lineBetween(x0: number, y0: number, x1: number, y1: number): this {
    this.mesh.lineBetween(x0, y0, x1, y1, this.lineWidth, this.lineTint);
    return this;
  }

  /** Everything painted so far as upload-ready chunks. */
  finish(): MeshChunk[] {
    return this.mesh.finish();
  }
}
